import { SearchIcon } from '@chakra-ui/icons';
import { Badge, Box, Button, Checkbox, Flex, Heading, Icon, Input, InputGroup, InputLeftElement, Popover, PopoverBody, PopoverContent, PopoverTrigger, Select, Table, Tag, TagCloseButton, TagLabel, Tbody, Td, Text, Th, Thead, Tr, useColorModeValue, useDisclosure, VStack } from '@chakra-ui/react';
import { useMemo, useState } from 'react';

import {
    createColumnHelper,
    flexRender,
    getCoreRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    PaginationState,
    SortingState,
    useReactTable
} from '@tanstack/react-table';

// Custom components
import Card from 'components/card/Card';
import EmptyState from 'components/states/EmptyState';
import ErrorState from 'components/states/ErrorState';
import InlineErrorState from 'components/states/InlineErrorState';
import SortIndicator from 'components/table/SortIndicator';
import Loader from '../../../components/Loader';
import MainMenu from 'components/menu/MainMenu';
import { MdFilterList, MdOpenInNew, MdPeople } from 'react-icons/md';
import TableRowActions from 'components/table/TableRowActions';
import TableDensityToggle from 'components/table/TableDensityToggle';
import TablePagination, { DEFAULT_TABLE_PAGE_SIZE } from 'components/table/TablePagination';
import { densityTokens, type TableDensity } from '../../../theme/chart-config';
import { PeopleIllustration } from 'components/illustrations';
import { translate } from '../../../i18n';
import { UserDto } from '../../../models/user/dto/user.dto';
import { tableHeaderTextProps, tableProps, tableThProps, tableThFlexProps, tableRowProps, tableCellProps } from '../../../theme/constants';
import { typography, useSemanticColor } from 'theme/tokens';
import ScopePrompt from '../../_shared/ScopePrompt';
import { useNavigateToUserDetails } from '../../_shared/calendar/hooks/useNavigateToUserDetails';
import { classifyRead } from '../../_shared/readState';
import { useSelectedOrganization } from '../../organization/hooks/useSelectedOrganization';
import { useGetCohorts } from '../../user-cohort/hooks/useGetCohorts';
import SelectCohortModal from '../../user-cohort/components/SelectCohortModal';
import { ActivityFilter, useGetUsers } from '../hooks/useGetUsers';
import BatchUploadUsersModal from './BatchUploadUsersModal';
import { matchesNameQuery } from '../../../utils/name-search';
import { isUnreadable } from '../../analytics/utils/statValue';

type ActivityStatus = 'all' | 'active' | 'inactive';
type TimeframeDays = '30' | '60' | '90';
type EmailStatusFilter = 'all' | 'delivered' | 'pending' | 'bounced' | 'failed' | 'not_sent';
type DocumentFilter =
    | 'all'
    | 'uploaded'
    | 'missing'
    | 'pending'
    | 'verified'
    | 'rejected'
    | 'needs_review';

function buildActivityFilter(status: ActivityStatus, timeframeDays: TimeframeDays, cohortId?: string): ActivityFilter | undefined {
    const hasActivityFilter = status !== 'all';
    const hasCohortFilter = Boolean(cohortId);

    if (!hasActivityFilter && !hasCohortFilter) return undefined;

    const filter: ActivityFilter = {};

    if (hasActivityFilter) {
        const activeAfter = new Date();
        activeAfter.setDate(activeAfter.getDate() - Number(timeframeDays));
        filter.activeAfter = activeAfter.toISOString();
        filter.inactive = status === 'inactive';
    }

    if (hasCohortFilter) {
        filter.cohortId = cohortId;
    }

    return filter;
}

function formatLastActive(dateStr?: string): string {
    if (!dateStr) return translate('user.never') || 'Never';
    const date = new Date(dateStr);
    return date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

type ActivitySeverity = 'recent' | 'medium' | 'stale' | 'inactive';

function getLastActiveSeverity(dateStr?: string): ActivitySeverity {
    if (!dateStr) return 'inactive';
    const date = new Date(dateStr);
    const now = new Date();
    const daysSince = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));
    if (daysSince <= 7) return 'recent';
    if (daysSince <= 30) return 'medium';
    if (daysSince <= 90) return 'stale';
    return 'inactive';
}

const severityColorScheme: Record<ActivitySeverity, string> = {
    recent: 'green',
    medium: 'yellow',
    stale: 'orange',
    inactive: 'red',
};

const columnHelper = createColumnHelper<UserDto>();

// MYS-72 — the "Adhésion" cell. `GET users` sends the active membership type
// names newest-first, or the organization's default type alone with
// `isDefaultMembership` when the user holds none (D3/D4). The joined string is
// also the column's sort key; '' (no membership, no default, or an API that
// predates the fields) renders as "—".
export const MEMBERSHIP_NAME_SEPARATOR = ' · ';

export function membershipLabel(user: Pick<UserDto, 'membershipTypeNames'>): string {
    return (user.membershipTypeNames ?? []).join(MEMBERSHIP_NAME_SEPARATOR);
}

export default function UserList() {
    const { selectedOrganization } = useSelectedOrganization();

    const [activityStatus, setActivityStatus] = useState<ActivityStatus>('all');
    const [timeframeDays, setTimeframeDays] = useState<TimeframeDays>('30');
    const [cohortFilter, setCohortFilter] = useState<string>('');
    const [levelFilter, setLevelFilter] = useState<string>('');
    const [membershipFilter, setMembershipFilter] = useState<string>('');
    const activityFilter = useMemo(
        () => buildActivityFilter(activityStatus, timeframeDays, cohortFilter || undefined),
        [activityStatus, timeframeDays, cohortFilter],
    );

    const { data, isInitialLoading, isError, error } = useGetUsers(selectedOrganization?.id, activityFilter);
    // `enabled: Boolean(organizationId) && …`. The loader below used to read
    // `isLoading && !data`, and React Query v4 holds `isLoading: true` on a
    // DISABLED query forever, so the roster spun under an empty table for any
    // session with no organization selected. The two EmptyState arms further
    // down are already guarded by `data &&`, so they were never the wrong half
    // here — the spinner was.
    const readState = classifyRead(isError, isInitialLoading, data);
    // R94-1 — `isError` is taken as well as `data`. The roster read above
    // already renders `<ErrorState>` on failure; the cohorts read had no such
    // branch, so a refused `GET /cohorts` left this page's cohort FILTER
    // holding only "all cohorts" — the same sentence an organization with no
    // cohorts produces. The rows themselves come from `useGetUsers`, which has
    // its own error state, so the filter states the failure rather than
    // withholding the table (R92-4's precedent for a list that is named, not
    // populated, by the failed read).
    const { data: cohorts, isError: isCohortsError } = useGetCohorts(selectedOrganization?.id);

    const [sorting, setSorting] = useState<SortingState>([]);
    // Client-side pages. The roster arrives whole (`GET /users`, up to ~5,000
    // rows) and this table used to render EVERY row at once: 4,000 users × 9
    // Chakra cells blocked the main thread for ~7.7 s on an org switch in a
    // CPU profile, which is the second half of the dashboard freeze a platform
    // admin hit moving between organizations (the first half is in
    // `useGetUsers`). Filtering, sorting and "select all" still operate on the
    // full list — only what is COMMITTED to the DOM is one page.
    const [pagination, setPagination] = useState<PaginationState>({
        pageIndex: 0,
        pageSize: DEFAULT_TABLE_PAGE_SIZE,
    });
    const [searchQuery, setSearchQuery] = useState('');
    const [emailStatusFilter, setEmailStatusFilter] = useState<EmailStatusFilter>('all');
    // Bulk-imported clients: 'pending' = imported but never signed in (claimedAt null),
    // 'signed_in' = imported and has since onboarded.
    const [importFilter, setImportFilter] = useState<'all' | 'pending' | 'signed_in'>('all');
    const [density, setDensity] = useState<TableDensity>('comfortable');
    const [documentFilter, setDocumentFilter] = useState<DocumentFilter>('all');
    const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
    const signupDocumentEnabled = selectedOrganization?.signupDocumentUploadEnabled ?? false;
    const textColor = useColorModeValue('secondaryGray.900', 'white');
    const textMuted = useSemanticColor('textMuted');
    const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');
    const navigateToUserDetails = useNavigateToUserDetails();

    const { isOpen: isAddToCohortOpen, onOpen: onAddToCohortOpen, onClose: onAddToCohortClose } = useDisclosure();
    const { isOpen: isImportUsersOpen, onOpen: onImportUsersOpen, onClose: onImportUsersClose } = useDisclosure();

    const uniqueLevels = useMemo(() => {
        if (!data) return [];
        return [...new Set(data.map((u) => u.level).filter((l) => l != null))].sort((a, b) => a - b);
    }, [data]);

    const uniqueMembershipNames = useMemo(() => {
        if (!data) return [];
        // A blank type name (the API's name validator is advisory) would become a
        // second `value=""` option, i.e. a duplicate of "all users".
        return [...new Set(data.flatMap((u) => u.membershipTypeNames ?? []))]
            .filter((name) => name !== '')
            .sort((a, b) => a.localeCompare(b));
    }, [data]);

    const activeFilterCount = useMemo(() => {
        let count = 0;
        if (activityStatus !== 'all') count++;
        if (emailStatusFilter !== 'all') count++;
        if (levelFilter !== '') count++;
        if (membershipFilter !== '') count++;
        if (cohortFilter !== '') count++;
        if (documentFilter !== 'all') count++;
        if (importFilter !== 'all') count++;
        return count;
    }, [activityStatus, emailStatusFilter, levelFilter, membershipFilter, cohortFilter, documentFilter, importFilter]);

    // Filter data based on search criteria
    const filteredData = useMemo(() => {
        if (!data) return [];

        return data.filter((user) => {
            const searchMatch = matchesNameQuery(searchQuery, user);
            const emailStatusMatch = emailStatusFilter === 'all' ||
                (emailStatusFilter === 'not_sent' ? !user.emailDeliveryStatus : user.emailDeliveryStatus === emailStatusFilter);
            const levelMatch = levelFilter === '' || String(user.level) === levelFilter;
            const membershipMatch = membershipFilter === '' || (user.membershipTypeNames ?? []).includes(membershipFilter);
            const hasDoc = Boolean(user.hasSignupDocument);
            const docStatus = user.signupDocumentStatus;
            // Two filter "axes" in one dropdown:
            // - 'uploaded'/'missing' are presence checks (legacy filters)
            // - the four verdict values match docStatus exactly
            // 'all' short-circuits.
            const documentMatch =
                documentFilter === 'all' ||
                (documentFilter === 'uploaded' && hasDoc) ||
                (documentFilter === 'missing' && !hasDoc) ||
                (documentFilter === 'pending' && docStatus === 'pending') ||
                (documentFilter === 'verified' && docStatus === 'verified') ||
                (documentFilter === 'rejected' && docStatus === 'rejected') ||
                (documentFilter === 'needs_review' && docStatus === 'needs_review');

            const importMatch =
                importFilter === 'all' ||
                (importFilter === 'pending' && Boolean(user.createdFromExport) && !user.claimedAt) ||
                (importFilter === 'signed_in' && Boolean(user.createdFromExport) && Boolean(user.claimedAt));

            return searchMatch && emailStatusMatch && levelMatch && membershipMatch && documentMatch && importMatch;
        });
    }, [data, searchQuery, emailStatusFilter, levelFilter, membershipFilter, documentFilter, importFilter]);

    const handleToggleUser = (userId: string) => {
        setSelectedUserIds((prev) =>
            prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId]
        );
    };

    const handleToggleAll = () => {
        if (selectedUserIds.length === filteredData.length) {
            setSelectedUserIds([]);
        } else {
            setSelectedUserIds(filteredData.map((u) => u.id));
        }
    };

    const handleAddToCohort = () => {
        onAddToCohortOpen();
    };

    const columns = useMemo(() => [
        columnHelper.display({
            id: 'select',
            header: () => (
                <Checkbox
                    isChecked={filteredData.length > 0 && selectedUserIds.length === filteredData.length}
                    isIndeterminate={selectedUserIds.length > 0 && selectedUserIds.length < filteredData.length}
                    onChange={handleToggleAll}
                />
            ),
            cell: (info: any) => (
                <Checkbox
                    isChecked={selectedUserIds.includes(info.row.original.id)}
                    onChange={(e) => {
                        e.stopPropagation();
                        handleToggleUser(info.row.original.id);
                    }}
                    onClick={(e) => e.stopPropagation()}
                />
            ),
        }),
        columnHelper.accessor('id', {
            id: 'id',
            header: () => (
                <Text
                    {...tableHeaderTextProps}>
                    ID
                </Text>
            ),
            cell: (info: any) => (
                <Flex align='center'>
                    <Text color={textColor} fontSize='sm' fontWeight='700'>
                        {info.getValue()}
                    </Text>
                </Flex>
            )
        }),
        columnHelper.accessor('firstName', {
            id: 'firstName',
            header: () => (
                <Text
                    {...tableHeaderTextProps}>
                    {translate('user.first_name')}
                </Text>
            ),
            cell: (info: any) => {
                const rowUser = info.row.original as UserDto;
                return (
                    <Flex align='center' gap={2}>
                        <Text color={textColor} fontSize='sm' fontWeight='700'>
                            {info.getValue()}
                        </Text>
                        {rowUser.createdFromExport && (
                            <Badge
                                colorScheme={rowUser.claimedAt ? 'green' : 'orange'}
                                variant='subtle'
                                fontSize='0.65rem'
                                textTransform='none'
                                title={rowUser.claimedAt ? 'Imported client, has signed in' : 'Imported client, has not signed in yet'}
                            >
                                {rowUser.claimedAt ? 'Imported' : 'Imported · not signed in'}
                            </Badge>
                        )}
                    </Flex>
                );
            }
        }),
        columnHelper.accessor('lastName', {
            id: 'lastName',
            header: () => (
                <Text
                    {...tableHeaderTextProps}>
                    {translate('user.last_name')}
                </Text>
            ),
            cell: (info: any) => (
                <Flex align='center'>
                    <Text color={textColor} fontSize='sm' fontWeight='700'>
                        {info.getValue()}
                    </Text>
                </Flex>
            )
        }),
        columnHelper.accessor('email', {
            id: 'email',
            header: () => (
                <Text
                    {...tableHeaderTextProps}>
                    {translate('user.email')}
                </Text>
            ),
            cell: (info: any) => (
                <Flex align='center'>
                    <Text color={textColor} fontSize='sm' fontWeight='700' maxW="250px" isTruncated title={info.getValue()}>
                        {info.getValue()}
                    </Text>
                </Flex>
            )
        }),
        columnHelper.accessor((row) => row.contactInfo?.phoneNumber, {
            id: 'phoneNumber',
            header: () => (
                <Text
                    {...tableHeaderTextProps}>
                    {translate('user.phone_number')}
                </Text>
            ),
            cell: (info: any) => (
                <Flex align='center'>
                    <Text color={textColor} fontSize='sm' fontWeight='700'>
                        {info.getValue() || '-'}
                    </Text>
                </Flex>
            )
        }),
        columnHelper.accessor('emailDeliveryStatus', {
            id: 'emailDeliveryStatus',
            header: () => (
                <Text
                    {...tableHeaderTextProps}>
                    {translate('user.welcome_email')}
                </Text>
            ),
            cell: (info) => {
                const status = info.getValue();
                const colorScheme = status === 'delivered' ? 'green'
                    : status === 'pending' ? 'yellow'
                    : status === 'bounced' || status === 'failed' ? 'red'
                    : 'gray';
                const label = status === 'delivered' ? translate('user.email_delivered')
                    : status === 'pending' ? translate('user.email_pending')
                    : status === 'bounced' ? translate('user.email_bounced')
                    : status === 'failed' ? translate('user.email_failed')
                    : translate('user.email_not_sent');
                return (
                    <Badge colorScheme={colorScheme} fontSize='xs' px={2} py={1} borderRadius='md'>
                        {label}
                    </Badge>
                );
            }
        }),
        columnHelper.accessor('level', {
            id: 'level',
            header: () => (
                <Text
                    {...tableHeaderTextProps}>
                    {translate('user.level')}
                </Text>
            ),
            cell: (info) => (
                <Text color={textColor} fontSize='sm' fontWeight='700'>
                    {info.getValue()}
                </Text>
            )
        }),
        columnHelper.accessor((row) => membershipLabel(row), {
            id: 'membership',
            header: () => (
                <Text
                    {...tableHeaderTextProps}>
                    {translate('user.membership')}
                </Text>
            ),
            cell: (info) => {
                const label = info.getValue();
                if (!label) {
                    return (
                        <Text color={textMuted} fontSize='sm'>
                            —
                        </Text>
                    );
                }
                // D4 — the organization's default type stands in for "no active
                // membership", so it is shown muted rather than as a badge.
                if (info.row.original.isDefaultMembership) {
                    return (
                        <Text color={textMuted} fontSize='sm' data-testid='membership-default'>
                            {label}
                        </Text>
                    );
                }
                return (
                    <Badge colorScheme='brand' fontSize='xs' px={2} py={1} borderRadius='md' textTransform='none'>
                        {label}
                    </Badge>
                );
            }
        }),
        columnHelper.accessor('lastActiveAt', {
            id: 'lastActiveAt',
            header: () => (
                <Text
                    {...tableHeaderTextProps}>
                    {translate('user.last_active_at')}
                </Text>
            ),
            cell: (info) => {
                const value = info.getValue();
                const severity = getLastActiveSeverity(value);
                return (
                    <Badge colorScheme={severityColorScheme[severity]} fontSize='xs' px={2} py={1} borderRadius='md'>
                        {formatLastActive(value)}
                    </Badge>
                );
            }
        }),
        ...(signupDocumentEnabled ? [columnHelper.accessor((row) => row.hasSignupDocument, {
            id: 'signupDocument',
            header: () => (
                <Text {...tableHeaderTextProps}>
                    {translate('user.signup_document')}
                </Text>
            ),
            cell: (info: any) => {
                const hasDoc = Boolean(info.getValue());
                if (!hasDoc) {
                    return (
                        <Badge colorScheme='gray' fontSize='xs' px={2} py={1} borderRadius='md'>
                            {translate('user.document_missing')}
                        </Badge>
                    );
                }
                // When verification is configured, show the verdict instead of
                // generic "uploaded". Falls back to "uploaded" for orgs that
                // haven't turned on verification yet.
                const status: string | null | undefined = info.row.original.signupDocumentStatus;
                const variants: Record<string, { colorScheme: string; label: string }> = {
                    pending: { colorScheme: 'yellow', label: translate('organization.document_status_pending') },
                    verified: { colorScheme: 'green', label: translate('organization.document_status_verified') },
                    rejected: { colorScheme: 'red', label: translate('organization.document_status_rejected') },
                    needs_review: { colorScheme: 'orange', label: translate('organization.document_status_needs_review') },
                };
                const variant = status ? variants[status] : null;
                // Neutral (gray) fallback for null/unknown status — green
                // would be a false positive implying "verified" when we
                // simply don't have a verdict yet (e.g. org without
                // verification enabled, or a future status the dashboard
                // hasn't been updated for).
                return (
                    <Badge
                        colorScheme={variant?.colorScheme ?? 'gray'}
                        fontSize='xs'
                        px={2}
                        py={1}
                        borderRadius='md'
                    >
                        {variant?.label ?? translate('user.document_uploaded')}
                    </Badge>
                );
            },
        })] : []),
        columnHelper.display({
            id: 'actions',
            header: () => null,
            cell: (info) => (
                <TableRowActions actions={[
                    { icon: MdOpenInNew, label: translate('common.view' as any) || 'View', onClick: () => navigateToUserDetails?.(info.row.original.id) },
                ]} />
            ),
        }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    ], [textColor, textMuted, selectedUserIds, filteredData, signupDocumentEnabled]);
    const table = useReactTable({
        data: filteredData,
        columns,
        state: {
            sorting,
            pagination,
        },
        initialState: {
            columnVisibility: {
                id: false
            }
        },
        onSortingChange: setSorting,
        onPaginationChange: setPagination,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        // `autoResetPageIndex` (the default) sends the operator back to page 1
        // whenever `filteredData` changes — a new search, filter, or
        // organization — so a page index from the previous org can never
        // point past the end of the next one's roster.
        getPaginationRowModel: getPaginationRowModel(),
    });
    return (
        <>
        <Card flexDirection='column' w='100%' px='0px' overflowX={{ sm: 'scroll', lg: 'hidden' }}>
            <Flex px={6} mb="8px" justifyContent='space-between' align='center'>
                <Flex align='center' gap={4}>
                    <Heading as="h2" color={textColor} {...typography.pageTitle}>
                        {translate('tabs.users')}
                    </Heading>
                    {selectedUserIds.length > 0 && (
                        <Badge colorScheme='brand' fontSize='md' px={3} py={1}>
                            {selectedUserIds.length} {translate('user.selected')}
                        </Badge>
                    )}
                </Flex>
                <Flex gap={2} align='center'>
                    {selectedUserIds.length > 0 && (
                        <Button
                            colorScheme='brand'
                            size='sm'
                            onClick={handleAddToCohort}
                        >
                            {translate('user.add_to_cohort')}
                        </Button>
                    )}
                    <Button
                        colorScheme='brand'
                        variant='outline'
                        size='sm'
                        onClick={onImportUsersOpen}
                    >
                        {translate('user.import_users')}
                    </Button>
                    <MainMenu />
                </Flex>
            </Flex>

            {/* Search & Filters */}
            <Flex px={6} mb={4} gap={3} align="center" flexWrap="wrap">
                <InputGroup maxW="340px" size="sm" h="40px" flex="1" minW="200px">
                    <InputLeftElement pointerEvents='none' h="40px" w="36px">
                        <SearchIcon color='gray.400' boxSize={3} />
                    </InputLeftElement>
                    <Input
                        placeholder={translate('user.search_placeholder' as any) || 'Search by name or email...'}
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        borderRadius="16px"
                        h="40px"
                        pl="36px"
                    />
                </InputGroup>

                <Popover placement="bottom-end">
                    <PopoverTrigger>
                        <Button
                            variant={activeFilterCount > 0 ? 'solid' : 'outline'}
                            colorScheme={activeFilterCount > 0 ? 'brand' : undefined}
                            size="sm"
                            h="40px"
                            borderRadius="16px"
                            leftIcon={<Icon as={MdFilterList} />}
                            position="relative"
                        >
                            {translate('user.filters') || 'Filters'}
                            {activeFilterCount > 0 && (
                                <Badge
                                    colorScheme={activeFilterCount > 0 ? 'white' : 'brand'}
                                    bg="white"
                                    color="brand.500"
                                    borderRadius="full"
                                    ml={2}
                                    fontSize="xs"
                                    minW="20px"
                                    textAlign="center"
                                >
                                    {activeFilterCount}
                                </Badge>
                            )}
                        </Button>
                    </PopoverTrigger>
                    <PopoverContent borderRadius="16px" w="300px" p={2}>
                        <PopoverBody>
                            <VStack spacing={3} align="stretch">
                                <Select
                                    value={activityStatus}
                                    onChange={(e) => setActivityStatus(e.target.value as ActivityStatus)}
                                    size="sm"
                                    borderRadius="12px"
                                    h="36px"
                                >
                                    <option value="all">{translate('user.activity_status')}: {translate('user.all_users')}</option>
                                    <option value="active">{translate('user.active')}</option>
                                    <option value="inactive">{translate('user.inactive')}</option>
                                </Select>

                                {activityStatus !== 'all' && (
                                    <Select
                                        value={timeframeDays}
                                        onChange={(e) => setTimeframeDays(e.target.value as TimeframeDays)}
                                        size="sm"
                                        borderRadius="12px"
                                        h="36px"
                                    >
                                        <option value="30">{translate('user.last_30_days')}</option>
                                        <option value="60">{translate('user.last_60_days')}</option>
                                        <option value="90">{translate('user.last_90_days')}</option>
                                    </Select>
                                )}

                                <Select
                                    value={emailStatusFilter}
                                    onChange={(e) => setEmailStatusFilter(e.target.value as EmailStatusFilter)}
                                    size="sm"
                                    borderRadius="12px"
                                    h="36px"
                                >
                                    <option value="all">{translate('user.welcome_email')}: {translate('user.all_users')}</option>
                                    <option value="delivered">{translate('user.email_delivered')}</option>
                                    <option value="pending">{translate('user.email_pending')}</option>
                                    <option value="bounced">{translate('user.email_bounced')}</option>
                                    <option value="failed">{translate('user.email_failed')}</option>
                                    <option value="not_sent">{translate('user.email_not_sent')}</option>
                                </Select>

                                <Select
                                    value={levelFilter}
                                    onChange={(e) => setLevelFilter(e.target.value)}
                                    size="sm"
                                    borderRadius="12px"
                                    h="36px"
                                >
                                    <option value="">{translate('user.level')}: {translate('user.all_levels')}</option>
                                    {uniqueLevels.map((level) => (
                                        <option key={level} value={String(level)}>
                                            {translate('user.level')} {level}
                                        </option>
                                    ))}
                                </Select>

                                {uniqueMembershipNames.length > 0 && (
                                    <Select
                                        value={membershipFilter}
                                        onChange={(e) => setMembershipFilter(e.target.value)}
                                        size="sm"
                                        borderRadius="12px"
                                        h="36px"
                                    >
                                        <option value="">{translate('user.membership')}: {translate('user.all_users')}</option>
                                        {uniqueMembershipNames.map((name) => (
                                            <option key={name} value={name}>
                                                {name}
                                            </option>
                                        ))}
                                    </Select>
                                )}

                                {/* `isUnreadable`, not the raw flag: React Query KEEPS `cohorts` through
                                    a failed background refetch, and this arm REPLACES the control. Reading
                                    the flag alone took the cohort filter away from the operator
                                    because a refresh was refused — and `cohortFilter` stays in
                                    state, so the table went on being filtered by a cohort with
                                    no visible control to clear it
                                    (useGetCohorts, staleTime 30s — R132-1). */}
                                {isUnreadable(isCohortsError, cohorts) ? (
                                    <InlineErrorState message={translate('cohort.error_loading')} />
                                ) : (
                                    <Select
                                        value={cohortFilter}
                                        onChange={(e) => setCohortFilter(e.target.value)}
                                        size="sm"
                                        borderRadius="12px"
                                        h="36px"
                                    >
                                        <option value="">{translate('user.cohort')}: {translate('user.all_cohorts')}</option>
                                        {cohorts?.map((cohort) => (
                                            <option key={cohort.id} value={cohort.id}>
                                                {cohort.name}
                                            </option>
                                        ))}
                                    </Select>
                                )}

                                {signupDocumentEnabled && (
                                    <Select
                                        value={documentFilter}
                                        onChange={(e) => setDocumentFilter(e.target.value as DocumentFilter)}
                                        size="sm"
                                        borderRadius="12px"
                                        h="36px"
                                    >
                                        <option value="all">{translate('user.signup_document')}: {translate('user.all_users')}</option>
                                        <option value="uploaded">{translate('user.document_uploaded')}</option>
                                        <option value="missing">{translate('user.document_missing')}</option>
                                        <option value="pending">Pending</option>
                                        <option value="verified">Verified</option>
                                        <option value="rejected">Rejected</option>
                                        <option value="needs_review">Needs review</option>
                                    </Select>
                                )}

                                <Select
                                    value={importFilter}
                                    onChange={(e) => setImportFilter(e.target.value as 'all' | 'pending' | 'signed_in')}
                                    size="sm"
                                    borderRadius="12px"
                                    h="36px"
                                >
                                    <option value="all">Import status: All</option>
                                    <option value="pending">Imported · not signed in</option>
                                    <option value="signed_in">Imported · signed in</option>
                                </Select>
                            </VStack>
                        </PopoverBody>
                    </PopoverContent>
                </Popover>
                <TableDensityToggle tableId="users" onChange={setDensity} />
            </Flex>

            {/* Active filter chips */}
            {activeFilterCount > 0 && (
                <Flex px={6} mb={3} gap={2} flexWrap="wrap" align="center">
                    {activityStatus !== 'all' && (
                        <Tag size="sm" variant="subtle" colorScheme="brand" borderRadius="full">
                            <TagLabel>{translate(`user.${activityStatus}`)} ({timeframeDays}d)</TagLabel>
                            <TagCloseButton onClick={() => setActivityStatus('all')} />
                        </Tag>
                    )}
                    {emailStatusFilter !== 'all' && (
                        <Tag size="sm" variant="subtle" colorScheme="brand" borderRadius="full">
                            <TagLabel>{translate(`user.email_${emailStatusFilter}` as any)}</TagLabel>
                            <TagCloseButton onClick={() => setEmailStatusFilter('all')} />
                        </Tag>
                    )}
                    {levelFilter !== '' && (
                        <Tag size="sm" variant="subtle" colorScheme="brand" borderRadius="full">
                            <TagLabel>{translate('user.level')} {levelFilter}</TagLabel>
                            <TagCloseButton onClick={() => setLevelFilter('')} />
                        </Tag>
                    )}
                    {membershipFilter !== '' && (
                        <Tag size="sm" variant="subtle" colorScheme="brand" borderRadius="full">
                            <TagLabel>{translate('user.membership')}: {membershipFilter}</TagLabel>
                            <TagCloseButton onClick={() => setMembershipFilter('')} />
                        </Tag>
                    )}
                    {cohortFilter !== '' && (
                        <Tag size="sm" variant="subtle" colorScheme="brand" borderRadius="full">
                            {/* The fallback is reached when the cohorts read
                                failed while a cohort filter was already
                                applied: the filter still narrows the table, so
                                the chip must stay clearable even though we can
                                no longer name it. Was a hardcoded 'Cohort'. */}
                            <TagLabel>{cohorts?.find(c => c.id === cohortFilter)?.name ?? translate('user.cohort')}</TagLabel>
                            <TagCloseButton onClick={() => setCohortFilter('')} />
                        </Tag>
                    )}
                    {documentFilter !== 'all' && (
                        <Tag size="sm" variant="subtle" colorScheme="brand" borderRadius="full">
                            <TagLabel>{documentFilterLabel(documentFilter)}</TagLabel>
                            <TagCloseButton onClick={() => setDocumentFilter('all')} />
                        </Tag>
                    )}
                    <Button
                        variant="link"
                        size="xs"
                        color="brand.500"
                        onClick={() => {
                            setActivityStatus('all');
                            setEmailStatusFilter('all');
                            setLevelFilter('');
                            setMembershipFilter('');
                            setCohortFilter('');
                            setDocumentFilter('all');
                        }}
                    >
                        {translate('common.clear_all' as any) || 'Clear all'}
                    </Button>
                </Flex>
            )}

            <Box>
                <Table {...tableProps}>
                    <Thead>
                        {table.getHeaderGroups().map((headerGroup) => (
                            <Tr key={headerGroup.id}>
                                {headerGroup.headers.map((header) => {
                                    return (
                                        <Th
                                            key={header.id}
                                            colSpan={header.colSpan}
                                            {...tableThProps}
                                            borderColor={borderColor}
                                            cursor='pointer'
                                            onClick={header.column.getToggleSortingHandler()}>
                                            <Flex
                                                {...tableThFlexProps}>
                                                {flexRender(header.column.columnDef.header, header.getContext())}
                                                <SortIndicator direction={header.column.getIsSorted()} />
                                            </Flex>
                                        </Th>
                                    );
                                })}
                            </Tr>
                        ))}
                    </Thead>
                    <Tbody>
                        {table.getRowModel().rows.map((row) => {
                            return (
                                <Tr key={row.id}
                                    {...tableRowProps}
                                    role="group"
                                    onClick={() => {
                                        navigateToUserDetails?.(row.getValue("id"));
                                    }}>
                                    {row.getVisibleCells().map((cell) => {
                                        return (
                                            <Td
                                                key={cell.id}
                                                {...tableCellProps}
                                                fontSize={densityTokens[density].fontSize}
                                                py={densityTokens[density].cellPy}
                                                onClick={cell.column.id === 'select' ? (e) => e.stopPropagation() : undefined}
                                                cursor={cell.column.id === 'select' ? 'default' : undefined}>
                                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                            </Td>
                                        );
                                    })}
                                </Tr>
                            );
                        })}
                    </Tbody>
                </Table>
                <TablePagination
                    pageIndex={table.getState().pagination.pageIndex}
                    pageCount={table.getPageCount()}
                    totalCount={filteredData.length}
                    canPrevious={table.getCanPreviousPage()}
                    canNext={table.getCanNextPage()}
                    onPrevious={() => table.previousPage()}
                    onNext={() => table.nextPage()}
                />
                <Loader isLoading={readState === 'loading'} />
                {readState === 'never-issued' && <ScopePrompt gate='organization' />}
                {error && (
                    <ErrorState message={translate('user.error_loading')} />
                )}
                {filteredData.length === 0 && data && data.length === 0 && (
                    <EmptyState
                        illustration={<PeopleIllustration />}
                        title={translate('walkthrough.empty_users_title')}
                        description={translate('walkthrough.empty_users_description')}
                    />
                )}
                {filteredData.length === 0 && data && data.length > 0 && (
                    <Box textAlign="center" py={8}>
                        <Text color="gray.500">{translate('user.no_users_found')}</Text>
                    </Box>
                )}
            </Box>
        </Card>

        {selectedUserIds.length > 0 && (
            <SelectCohortModal
                userIds={selectedUserIds}
                isOpen={isAddToCohortOpen}
                onClose={() => {
                    onAddToCohortClose();
                    setSelectedUserIds([]);
                }}
            />
        )}

        {selectedOrganization && (
            <BatchUploadUsersModal
                isOpen={isImportUsersOpen}
                onClose={onImportUsersClose}
                organizationId={selectedOrganization.id}
            />
        )}
        </>
    );
}

function documentFilterLabel(filter: DocumentFilter): string {
    switch (filter) {
        case 'uploaded':
            return translate('user.document_uploaded');
        case 'missing':
            return translate('user.document_missing');
        case 'pending':
            return translate('organization.document_status_pending');
        case 'verified':
            return translate('organization.document_status_verified');
        case 'rejected':
            return translate('organization.document_status_rejected');
        case 'needs_review':
            return translate('organization.document_status_needs_review');
        default:
            return '';
    }
}